import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import LayerBadge, { LAYER_META } from "../components/LayerBadge";
import LifecyclePill from "../components/LifecyclePill";
import ServicePill from "../components/ServicePill";
import SeverityBadge from "../components/SeverityBadge";
import OpportunityDetailModal from "../components/OpportunityDetailModal";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { Layers, Handshake, Users } from "lucide-react";

function KpiBox({ label, value, sub, testId }) {
  return (
    <div className="border border-border p-3 rounded-sm bg-white" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="cs-kpi-value text-xl text-foreground mt-1">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function WasteGraph() {
  const [summary, setSummary] = useState(null);
  const [commitments, setCommitments] = useState(null);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLayer, setActiveLayer] = useState(0); // 0 = all
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, o] = await Promise.all([
        api.wastegraph.summary(),
        api.wastegraph.commitments(),
        api.opportunities({}),
      ]);
      setSummary(s);
      setCommitments(c);
      setOpps(o || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const chartData = useMemo(
    () =>
      (summary?.layers || []).map((l) => ({
        name: `L${l.layer} ${l.name}`,
        layer: l.layer,
        savings: l.monthly_savings_usd,
      })),
    [summary],
  );

  const filtered = useMemo(
    () => (activeLayer ? opps.filter((o) => o.layer === activeLayer) : opps),
    [opps, activeLayer],
  );

  if (loading && !summary) {
    return <div className="py-8 text-muted-foreground">Loading WasteGraph…</div>;
  }

  return (
    <div className="space-y-6" data-testid="wastegraph-page">
      {/* Layer cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="layer-cards">
        {(summary?.layers || []).map((l) => {
          const meta = LAYER_META[l.layer];
          const active = activeLayer === l.layer;
          return (
            <button
              key={l.layer}
              data-testid={`layer-card-${l.layer}`}
              onClick={() => setActiveLayer(active ? 0 : l.layer)}
              className={`cs-card p-4 text-left transition-colors duration-200 border-t-4 ${
                active ? "ring-2 ring-[#00579e]" : ""
              }`}
              style={{ borderTopColor: meta?.color }}
            >
              <div className="flex items-center justify-between">
                <LayerBadge layer={l.layer} />
                <span className="text-[11px] text-muted-foreground">{l.count} findings</span>
              </div>
              <div className="cs-heading text-sm font-semibold mt-2">{l.name}</div>
              <div className="cs-mono text-lg font-semibold text-[#00579e] mt-1">
                {fmtUSD(l.monthly_savings_usd)}<span className="text-[11px] text-muted-foreground font-normal">/mo</span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1 leading-snug">{l.description}</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Savings by layer */}
        <div className="cs-card p-5" data-testid="savings-by-layer-chart">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-[#00579e]" />
            <h2 className="cs-heading text-base font-semibold">Waste by Layer</h2>
            <span className="ml-auto text-xs text-muted-foreground">
              {summary?.total_findings} findings · <span className="cs-mono text-[#00579e] font-semibold">{fmtUSD(summary?.total_monthly_savings_usd)}</span>/mo
            </span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 30 }}>
              <XAxis type="number" tickFormatter={(v) => fmtUSD(v, { compact: true })} fontSize={11} />
              <YAxis type="category" dataKey="name" width={130} fontSize={11} />
              <Tooltip formatter={(v) => fmtUSD(v)} />
              <Bar dataKey="savings" radius={[0, 3, 3, 0]}>
                {chartData.map((d) => (
                  <Cell key={d.layer} fill={LAYER_META[d.layer]?.color || "#00579e"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Ownership */}
        <div className="cs-card p-5" data-testid="ownership-panel">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-[#00579e]" />
            <h2 className="cs-heading text-base font-semibold">Inferred Ownership</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.12em] text-muted-foreground">no manual tagging</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Team</th>
                <th className="py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Owner</th>
                <th className="py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Findings</th>
                <th className="py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Savings / mo</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.by_team || []).map((t) => (
                <tr key={t.team} className="border-b border-border last:border-0" data-testid={`team-row-${t.team}`}>
                  <td className="py-2 font-medium">{t.team}</td>
                  <td className="py-2 cs-mono text-[12px] text-muted-foreground">{t.owner}</td>
                  <td className="py-2 text-right">{t.count}</td>
                  <td className="py-2 text-right cs-mono font-semibold text-[#00579e]">{fmtUSD(t.monthly_savings_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Commitment Intelligence */}
      <div className="cs-card p-5" data-testid="commitment-panel">
        <div className="flex items-center gap-2 mb-4">
          <Handshake className="w-4 h-4 text-[#00579e]" />
          <h2 className="cs-heading text-base font-semibold">Commitment Intelligence</h2>
          <span className={`cs-pill cs-mono text-[10px] ml-2 ${commitments?.data_source === "live" ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#fef3c7] text-[#92400e]"}`}>
            {commitments?.data_source === "live" ? "LIVE" : "REFERENCE"}
          </span>
          <span className="ml-auto text-[11px] text-muted-foreground">last {commitments?.window_days} days · CUD / SUD analysis only (purchasing out of scope)</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <KpiBox testId="ci-eligible" label="Eligible Spend" value={fmtUSD(commitments?.eligible_spend_usd)} sub="Compute · SQL · GKE" />
          <KpiBox testId="ci-coverage" label="CUD Coverage" value={`${commitments?.coverage_pct ?? 0}%`} sub={`${fmtUSD(commitments?.cud_credits_usd)} credits`} />
          <KpiBox testId="ci-utilization" label="Utilization" value={commitments?.utilization_pct != null ? `${commitments.utilization_pct}%` : "—"} sub="of purchased commitments" />
          <KpiBox testId="ci-sud" label="SUD Credits" value={fmtUSD(commitments?.sud_credits_usd)} sub="sustained-use discounts" />
          <KpiBox testId="ci-potential" label="Savings Potential" value={fmtUSD(commitments?.potential_monthly_savings_usd)} sub="per month if covered" />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Type</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Scope</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Term</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Est. Savings / mo</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Risk</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Rationale</th>
            </tr>
          </thead>
          <tbody>
            {(commitments?.recommendations || []).length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground" data-testid="no-commitment-recs">Coverage is healthy — no commitment recommendations.</td></tr>
            )}
            {(commitments?.recommendations || []).map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0" data-testid={`commitment-rec-${r.id}`}>
                <td className="px-3 py-2 font-medium whitespace-nowrap">{r.commitment_type}</td>
                <td className="px-3 py-2 cs-mono text-[12px]">{r.scope}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.term}</td>
                <td className="px-3 py-2 text-right cs-mono font-semibold text-[#00579e]">{fmtUSD(r.est_monthly_savings_usd)}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${r.risk_band === "low" ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#fef3c7] text-[#92400e]"}`}>
                    {r.risk_band}
                  </span>
                </td>
                <td className="px-3 py-2 text-[12px] text-muted-foreground">{r.rationale}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Findings by layer */}
      <div className="cs-card overflow-hidden" data-testid="wastegraph-findings">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <h2 className="cs-heading text-base font-semibold">
            Findings {activeLayer ? `· Layer ${activeLayer} (${LAYER_META[activeLayer]?.name})` : "· All Layers"}
          </h2>
          {activeLayer > 0 && (
            <button
              data-testid="clear-layer-filter"
              onClick={() => setActiveLayer(0)}
              className="text-xs text-[#00579e] hover:underline"
            >
              clear filter
            </button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">{filtered.length} findings</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Layer</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Service</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Severity</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Finding</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Team</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Savings / mo</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No findings in this layer.</td></tr>
              )}
              {filtered.slice(0, 50).map((o) => (
                <tr
                  key={o.id}
                  className="cs-row-hover border-b border-border last:border-0 cursor-pointer"
                  data-testid={`wg-row-${o.id}`}
                  onClick={() => setSelected(o)}
                >
                  <td className="px-4 py-3"><LayerBadge layer={o.layer} /></td>
                  <td className="px-4 py-3"><ServicePill service={o.service} /></td>
                  <td className="px-4 py-3"><SeverityBadge severity={o.severity} /></td>
                  <td className="px-4 py-3 font-medium max-w-[320px] truncate" title={o.opportunity_type}>{o.opportunity_type}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{o.team}</td>
                  <td className="px-4 py-3"><LifecyclePill status={o.lifecycle_status} /></td>
                  <td className="px-4 py-3 text-right cs-mono font-semibold text-[#00579e]">{fmtUSD(o.estimated_monthly_savings_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <OpportunityDetailModal
        open={!!selected}
        onClose={() => setSelected(null)}
        opportunity={selected}
        onChanged={load}
      />
    </div>
  );
}
